import { findIndex, noop } from "lodash";
import AppContext from "PFApp/app_context";
import { usePage } from "PFApp/use_page";
import { GROWL_ACTIONS } from "PFCore/reducers/growl_reducer";
import { fetchAccount } from "PFCore/services/accounts/account";
import { fetchTemplates, saveActivityTemplateState } from "PFCore/services/templates";
import { Template } from "PFTypes";
import { useContext, useEffect } from "react";

type InitPage = {
  activityTemplates: {
    loaded: boolean;
    collection: Template[];
  };
  account: {
    name: string;
    loaded: boolean;
  } | null;
  fetch: Function | null;
  loaded: boolean;
  errors: string[];
  toggleActivityTemplate: () => void;
};

const initPage: InitPage = {
  activityTemplates: { loaded: false, collection: [] },
  account: null,
  fetch: null,
  loaded: false,
  errors: [],
  toggleActivityTemplate: noop
};

export const useActivityTemplatesListPage = (accountId: string) => {
  const { page, setPage, mergePage, resetPage } = usePage<InitPage>(`features`, initPage);
  const { dispatch } = useContext(AppContext);

  const fetch = (page = 1, perPage = 50) =>
    fetchTemplates({
      accountId,
      page,
      perPage
    })
      .then((response) =>
        mergePage({
          activityTemplates: {
            loaded: true,
            collection: response.entries
          }
        })
      )
      .catch(() =>
        mergePage({
          loaded: true,
          errors: ["there was an error"]
        })
      );

  const getAccount = () =>
    fetchAccount(accountId)
      .then(({ name }) =>
        mergePage({
          account: {
            loaded: true,
            name
          }
        })
      )
      .catch(() =>
        mergePage({
          loaded: true,
          errors: ["there was an error"]
        })
      );

  const toggleActivityTemplate = (activityTemplateId: string, state: boolean) =>
    saveActivityTemplateState(activityTemplateId, accountId, state)
      .then((response) => {
        const updatedActivityTemplates = [...page.activityTemplates.collection];
        const updatedIndex = findIndex(updatedActivityTemplates, ["id", response.id]);
        updatedActivityTemplates[updatedIndex] = response;

        mergePage({
          activityTemplates: {
            loaded: true,
            collection: updatedActivityTemplates
          }
        });

        return response;
      })
      .then((response) => {
        const type = GROWL_ACTIONS.GROWL_RENDER;

        dispatch({
          type,
          payload: { message: `Template toggled ${response.active ? "on" : "off"}`, kind: "success" }
        });
      })
      .catch(({ response }) => {
        const type = GROWL_ACTIONS.GROWL_RENDER;

        response.data.errors.forEach((error) => {
          dispatch({ type, payload: { message: error.detail, kind: "error" } });
        });
      });

  useEffect(() => {
    setPage(initPage);
    return resetPage;
  }, []);

  useEffect(() => {
    getAccount();
    fetch();
  }, []);

  return { ...page, fetch, toggleActivityTemplate };
};
